import { Buttons, DataBrowsing, MUIcon } from '@platform/shared/ui';
import { MetadataTypes, ProjectTypes } from '@platform/types';
import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useWorkspaceContext } from '../../../../contexts/WorkspaceContext';
import * as peApi from '../../../../pe.api';
import PanelHeader from '../../../MapSidebar/PanelHeader';
import Panel from '../../../shared/Panel';
import { getFormatting } from '../../Edit/SocialExplorer/utils';
import WorkingLayerTooltipColumn from '../Editor/WorkingLayerTooltipColumn';

const { DataBrowser } = DataBrowsing;

interface Props {
  workingLayer: ProjectTypes.SocialExplorerWorkingLayer;
  onTitleChange: (newTitle: ProjectTypes.TooltipLineItem) => void;
  onContentAdd: (newContentItem: ProjectTypes.TooltipLineItem) => void;
  onContentRemove: (id: string) => void;
  onContentLabelChange: (id: string, value: string) => void;
  onDragEnd: (result: DropResult) => void;
  onTitleVariableRemove: () => void;
}

const SocialExplorerWorkingLayerTooltipEditor: React.FC<Props> = ({
  workingLayer,
  onContentAdd,
  onContentRemove,
  onContentLabelChange,
  onDragEnd,
  onTitleVariableRemove,
}) => {
  const { activeWorkspace } = useWorkspaceContext();
  // TODO: does isUserSurvey needs specific handling??
  const { isUserSurvey } = workingLayer.metadata;

  const [columnPickerAction, setColumnPickerAction] = useState<'title' | 'content' | null>(null);

  const handleClick = (e: any) => {
    onTitleVariableRemove();
    e.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    onTitleVariableRemove();
  };

  const titleContent = workingLayer.tooltip?.title ? (
    <Buttons.Secondary
      className="text-primary-700 h-[54px] flex-row-reverse justify-between rounded-lg bg-gray-50 px-2 text-gray-700"
      onClick={() => {
        setColumnPickerAction('title');
      }}
      icon={
        <div
          role="button"
          tabIndex={0}
          className="flex items-center justify-center px-1"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          <MUIcon name="close" className="text-gray-600" />
        </div>
      }
    >
      <span className="flex items-start px-4 font-normal">{workingLayer.tooltip.title.variableId}</span>
    </Buttons.Secondary>
  ) : (
    <Buttons.Secondary
      className="text-primary-700 h-[34px] w-[110px] gap-1 rounded-[999px]"
      icon={<MUIcon name="add" />}
      iconClassName="pl-0.5"
      onClick={() => setColumnPickerAction('title')}
    >
      <span className="pr-1.5">Add title</span>
    </Buttons.Secondary>
  );

  const handleVariableChange = async (
    variableSelection: ProjectTypes.VariableSelection[],
    tableMetadata: MetadataTypes.Table
  ) => {
    const [selection] = variableSelection;
    const variableMetadata = tableMetadata.variables.find((v) => v.uuid === selection.variableGuid);

    if (!variableMetadata) {
      console.error('Variable Metadata missing');
      return;
    }

    switch (columnPickerAction) {
      case 'title':
        // TODO: is this possible at all. It might be wise to hide title options or we need a special UI for that. #Geo_QNAME
        // onTitleChange(payload);
        break;
      case 'content':
        onContentAdd({
          id: selection.variableGuid,
          label: variableMetadata.label,
          format: getFormatting(variableMetadata.formatting as ProjectTypes.ValueFormat),
          variableId: selection.variableGuid,
        });
        break;
    }
    setColumnPickerAction(null);
  };

  return (
    <>
      <div className="flex flex-col gap-6 py-4">
        <div className="items-left pointer-events-none flex w-full flex-col gap-3 opacity-50">
          <span className="text-sm text-gray-900">Title</span>
          {titleContent}
        </div>
        <div className="items-left flex w-full flex-col gap-3">
          <span className="text-sm text-gray-900">Content</span>
          {!!workingLayer.tooltip?.content?.length && (
            <div className="flex flex-col gap-3">
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="flex flex-grow flex-col gap-2.5  bg-white py-4"
                    >
                      {workingLayer.tooltip?.content.map((content, idx) => (
                        <Draggable key={content.id} draggableId={content.id} index={idx}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <WorkingLayerTooltipColumn
                                key={content.id}
                                tooltipContent={content}
                                onContentRemove={onContentRemove}
                                onContentLabelChange={onContentLabelChange}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}
          <div className="flex gap-3">
            <Buttons.Secondary
              className="text-primary-700 w-fit-content h-[34px] gap-1 rounded-[999px]"
              icon={<MUIcon name="add" />}
              iconClassName="pl-0.5"
              onClick={() => setColumnPickerAction('content')}
            >
              <span className="pr-1.5">Add variable</span>
            </Buttons.Secondary>
          </div>
        </div>
      </div>
      {columnPickerAction && (
        <Panel
          children={
            <div className="flex h-full flex-col overflow-y-auto">
              <PanelHeader title="Select a column" onBack={() => setColumnPickerAction(null)} />
              <DataBrowser
                isLoading={false}
                disableSurveyChange
                showDatasetPicker={false}
                surveysApi={peApi.getSurveys}
                onChange={handleVariableChange}
                activeWorkspaceId={activeWorkspace.id}
                allowMultiSelection={false}
                selection={workingLayer.variableSelection.map((x) => ({
                  variableGuid: '', //x.variableId,
                  tableGuid: '', //x.tableId,
                  universeVariableUuid: x.universeVariableId,
                  isUserSurvey: workingLayer.metadata.isUserSurvey,
                  datasetAbbrev: workingLayer.metadata.datasetAbbreviation,
                  surveyName: workingLayer.metadata.surveyName,
                }))}
              />
            </div>
          }
        />
      )}
    </>
  );
};

export default SocialExplorerWorkingLayerTooltipEditor;
